/**
 * Models of the hyperbolic plane: four pictures of the same space. Each model is described by a
 * map from its own plane to the Poincaré disk (`toDisk`) and back (`fromDisk`).
 *
 * Every model also has a one-parameter family that starts at the Poincaré disk (t = 0) and ends
 * at the model (t = 1), which the renderer uses to morph between pictures:
 *
 *   Klein       z = w / (1 + t·√(1 − |w|²))
 *   half-plane  z = (w − i) / (i + t·w)          (t = 1: inverse Cayley transform)
 *   band        z = tanh(t·w / s) / t,  s = 1 + t(4/π − 1)
 *
 * The half-plane family draws the disk centred at i at t = 0, so its camera is centred at i.
 */
import { type Complex, I, abs2, add, catanh, ctanh, cx, div, mul, scale, sub } from './complex';

export type ModelId = 'disk' | 'halfplane' | 'klein' | 'band';

export const MODELS: readonly { id: ModelId; label: string; short: string }[] = [
  { id: 'disk', label: 'Poincaré disk', short: 'Disk' },
  { id: 'halfplane', label: 'Upper half-plane', short: 'Half-plane' },
  { id: 'klein', label: 'Klein disk', short: 'Klein' },
  { id: 'band', label: 'Band', short: 'Band' },
];

export const MODEL_INDEX: Record<ModelId, number> = { disk: 0, klein: 1, halfplane: 2, band: 3 };

const BAND_SCALE = 4 / Math.PI;

/** Model plane → disk. Returns null when w lies outside the model's picture of the plane. */
export function toDisk(model: ModelId, w: Complex, t = 1): Complex | null {
  let z: Complex;
  switch (model) {
    case 'disk':
      z = w;
      break;
    case 'klein': {
      const s = 1 - abs2(w);
      if (s <= 0) return null;
      z = scale(w, 1 / (1 + t * Math.sqrt(s)));
      break;
    }
    case 'halfplane': {
      if (t >= 1 && w.im <= 0) return null;
      const den = add(I, scale(w, t));
      if (abs2(den) === 0) return null;
      z = div(sub(w, I), den);
      break;
    }
    case 'band': {
      if (t < 1e-6) {
        z = w;
        break;
      }
      const s = 1 + t * (BAND_SCALE - 1);
      if (t >= 1 && Math.abs(w.im) >= 1) return null;
      z = scale(ctanh(scale(w, t / s)), 1 / t);
      break;
    }
  }
  return abs2(z) < 1 ? z : null;
}

/** Disk → model plane (exact inverse of `toDisk` at t = 1; also valid for the half-plane and band families). */
export function fromDisk(model: ModelId, z: Complex, t = 1): Complex {
  switch (model) {
    case 'disk':
      return z;
    case 'klein': {
      if (t >= 1) return scale(z, 2 / (1 + abs2(z)));
      // Radial: |z| = ρ/(1 + t√(1−ρ²)); solve for ρ by Newton's method.
      const rz = Math.sqrt(abs2(z));
      if (rz === 0) return z;
      let rho = Math.min(0.999999, rz * (1 + t));
      for (let i = 0; i < 40; i++) {
        const sq = Math.sqrt(Math.max(1 - rho * rho, 0));
        const f = rho - rz * (1 + t * sq);
        const df = 1 + (rz * t * rho) / Math.max(sq, 1e-12);
        const next = Math.min(0.9999999, Math.max(0, rho - f / df));
        if (Math.abs(next - rho) < 1e-15) break;
        rho = next;
      }
      return scale(z, rho / rz);
    }
    case 'halfplane':
      // w = i(1 + z)/(1 − t·z)
      return div(mul(I, add(cx(1), z)), sub(cx(1), scale(z, t)));
    case 'band': {
      if (t < 1e-6) return z;
      const s = 1 + t * (BAND_SCALE - 1);
      return scale(catanh(scale(z, t)), s / t);
    }
  }
}
